import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Star, Heart } from 'lucide-react';
import { OfferBadge } from './OfferBadge';
export interface Hotel {
  id: string;
  name: string;
  location: string;
  rating: number;
  reviews: number;
  price: number;
  image: string;
  offer?: {
    text: string;
    type: 'discount' | 'deal' | 'limited';
  };
  amenities: string[];
}
interface HotelCardProps {
  hotel: Hotel;
}
export function HotelCard({
  hotel
}: HotelCardProps) {
  const handleViewDetails = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.hash = `hotel-${hotel.id}`;
  };
  return <motion.article className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col h-full" whileHover={{
    y: -4
  }} data-id="element-177">
      {/* Image Container */}
      <div className="relative aspect-[4/3] overflow-hidden" data-id="element-178">
        <motion.img src={hotel.image} alt={hotel.name} className="w-full h-full object-cover" whileHover={{
        scale: 1.1
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-179" />

        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-60" data-id="element-180" />

        {/* Top Badges */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-start" data-id="element-181">
          {hotel.offer ? <OfferBadge text={hotel.offer.text} type={hotel.offer.type} data-id="element-182" /> : <div data-id="element-183" />}

          <button className="p-2 rounded-full bg-white/90 text-gray-600 hover:text-rose-500 hover:bg-white transition-colors shadow-sm backdrop-blur-sm" aria-label="Add to favorites" data-id="element-184">
            <Heart className="w-4 h-4" data-id="element-185" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-grow" data-id="element-186">
        <div className="flex justify-between items-start mb-2" data-id="element-187">
          <div data-id="element-188">
            <h3 className="text-lg font-bold text-gray-900 leading-tight mb-1 group-hover:text-travel-blue-600 transition-colors" data-id="element-189">
              {hotel.name}
            </h3>
            <div className="flex items-center text-gray-500 text-sm" data-id="element-190">
              <MapPin className="w-3 h-3 mr-1" data-id="element-191" />
              {hotel.location}
            </div>
          </div>
        </div>

        <div className="flex items-center mb-4" data-id="element-192">
          <div className="flex items-center bg-amber-50 px-2 py-1 rounded-md" data-id="element-193">
            <Star className="w-3 h-3 text-amber-500 fill-amber-500 mr-1" data-id="element-194" />
            <span className="font-bold text-amber-700 text-sm" data-id="element-195">
              {hotel.rating}
            </span>
          </div>
          <span className="text-gray-400 text-xs ml-2" data-id="element-196">
            ({hotel.reviews.toLocaleString()} reviews)
          </span>
        </div>

        {/* Amenities Preview */}
        <div className="flex flex-wrap gap-2 mb-4" data-id="element-197">
          {hotel.amenities.slice(0, 3).map(amenity => <span key={amenity} className="text-[10px] px-2 py-1 bg-travel-blue-50 text-travel-blue-700 rounded-md border border-travel-blue-100" data-id="element-198">
              {amenity}
            </span>)}
          {hotel.amenities.length > 3 && <span className="text-[10px] px-2 py-1 bg-gray-50 text-gray-400 rounded-md border border-gray-100" data-id="element-199">
              +{hotel.amenities.length - 3}
            </span>}
        </div>

        <div className="mt-auto pt-4 border-t border-gray-50 flex items-end justify-between" data-id="element-200">
          <div data-id="element-201">
            <p className="text-xs text-gray-400 mb-0.5" data-id="element-202">Price per night</p>
            <div className="flex items-baseline" data-id="element-203">
              <span className="text-xl font-bold text-gray-900" data-id="element-204">
                ${hotel.price}
              </span>
              {hotel.offer && <span className="ml-2 text-sm text-gray-400 line-through" data-id="element-205">
                  ${Math.round(hotel.price * 1.2)}
                </span>}
            </div>
          </div>

          <button onClick={handleViewDetails} className="px-4 py-2 bg-travel-blue-600 text-white text-sm font-medium rounded-lg hover:bg-travel-blue-700 transition-colors shadow-sm" data-id="element-206">
            View Details
          </button>
        </div>
      </div>
    </motion.article>;
}